import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { SubmitIcon, AddGroupIcon } from 'src/v3/core/icons'

import theme from 'src/theme'
import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import Loading from 'src/v3/components/loading'
import {
  fetchGroups,
  selectActiveLinkedService,
  selectUsersStatus,
  fetchUsers,
  selectUsersByLinkedService,
} from '../manage.slice'
import { UserList } from '../components/entity-list'

let createUserGroup = (userGroup: {
  linkedServiceID: string
  name: string
  userIDs: string[]
}) => axios.post<unknown>('/api/v2/manage/group', userGroup).then(pluckData)

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let users = useAppSelector(selectUsersByLinkedService(activeLinkedService))
  let usersStatus = useAppSelector(selectUsersStatus)

  let [checked, setChecked] = useState<string[]>([])
  let [groupName, setGroupName] = useState('')
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus])

  if (activeLinkedService === 'unselected') {
    navigate('/manage/groups')
  }

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          p: 2,
          maxWidth: 480,
          bgcolor: 'background.paper',
          margin: 'auto',
        }}
      >
        <div className="flex items-center justify-center my-2">
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <AddGroupIcon />
          </Avatar>
          <Typography
            className="pl-2"
            variant="h4"
            style={{
              fontFamily: 'smileAndWave',
            }}
          >
            Create Group
          </Typography>
        </div>
        <Divider className="my-3" />
        <Typography variant="h5" textAlign="center">
          Group Name
        </Typography>
        <TextField
          variant="outlined"
          className="my-2 mb-4"
          label="Name"
          name="name"
          type="text"
          fullWidth
          value={groupName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setGroupName(event.target.value)
          }}
        />
        <Typography variant="h5" textAlign="center">
          Select Users
        </Typography>
        <UserList
          users={users}
          checkbox={{
            onClick: setChecked,
            checked,
          }}
        />
        <div className="flex justify-center mt-4">
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SubmitIcon />}
            disabled={!groupName || activeLinkedService === 'unselected'}
            onClick={() => {
              setSaving(true)
              createUserGroup({
                name: groupName,
                linkedServiceID: activeLinkedService,
                userIDs: checked,
              })
                .then(() => dispatch(fetchGroups()))
                .then(() => navigate('/manage/groups'))
                .catch((err) => {
                  setErrorMessage(err?.response?.data)
                  setSaving(false)
                })
            }}
          >
            Submit
          </Button>
          <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
            <Alert
              variant="filled"
              onClose={() => setErrorMessage('')}
              severity="error"
            >
              <strong>Error Creating Group: </strong>
              {errorMessage}
            </Alert>
          </Snackbar>
        </div>
      </Card>
      <Loading entities={[usersStatus]} loading={saving} />
    </>
  )
}
